/**
 * Pure-helpers для CirclePickerOverlay (Phase 7, ADR-0008).
 *
 * Извлечены из `CirclePickerOverlay.tsx` чтобы не нарушать
 * `react-refresh/only-export-components`: компонент-файл должен экспортировать
 * только React-компоненты, а helpers живут отдельно. Тесты импортируют отсюда
 * напрямую — jsdom не передаёт `clientX/Y` через `PointerEvent` init корректно,
 * поэтому полный E2E-сценарий drag тестируется в Phase 9 (Playwright).
 */

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Pure: client-px (e.clientX/Y) → image-pixel space.
 *
 * Учитывает letterbox: картинка показывается через `object-contain` /
 * SVG `preserveAspectRatio="xMidYMid meet"`, поэтому реально отображаемый
 * прямоугольник картинки может быть уже/ниже бокса оверлея (`rect`) и
 * отцентрован (pillarbox/letterbox). Маппинг считается относительно
 * РЕАЛЬНОГО displayed-прямоугольника, а не всего бокса — иначе на портретном
 * фото горизонталь «сжимается» и круг не доходит до краёв (pre-W2 баг).
 *
 * Когда аспект бокса == аспект картинки (типичный альбом, контейнер обнимает
 * фото) — offset=0, displayW/H=rect.width/height → совпадает с naive-маппингом.
 */
export function clientToImagePx(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
): { x: number; y: number } {
  if (rect.width === 0 || rect.height === 0 || imageWidth === 0 || imageHeight === 0) {
    return { x: clientX - rect.left, y: clientY - rect.top };
  }
  const boxAspect = rect.width / rect.height;
  const imgAspect = imageWidth / imageHeight;
  let displayW: number;
  let displayH: number;
  let offsetX: number;
  let offsetY: number;
  if (imgAspect > boxAspect) {
    // Картинка «шире» бокса → заполняет ширину, letterbox сверху/снизу.
    displayW = rect.width;
    displayH = rect.width / imgAspect;
    offsetX = 0;
    offsetY = (rect.height - displayH) / 2;
  } else {
    // Картинка «уже» бокса (портрет) → заполняет высоту, pillarbox слева/справа.
    displayH = rect.height;
    displayW = rect.height * imgAspect;
    offsetX = (rect.width - displayW) / 2;
    offsetY = 0;
  }
  return {
    x: ((clientX - rect.left - offsetX) / displayW) * imageWidth,
    y: ((clientY - rect.top - offsetY) / displayH) * imageHeight,
  };
}

/** Pure: clamp центра круга так, чтобы круг был целиком внутри image (ADR-0008 §3). */
export function clampCircleCenter(
  cx: number,
  cy: number,
  radius: number,
  imageWidth: number,
  imageHeight: number,
): { cx: number; cy: number } {
  return {
    cx: clamp(cx, radius, imageWidth - radius),
    cy: clamp(cy, radius, imageHeight - radius),
  };
}
